import { createClient, SupabaseClient, Session } from "@supabase/supabase-js";
import { Database } from "./database.types";

export const SUPABASE_URL = (import.meta.env.VITE_SUPABASE_URL ?? "").trim();
export const SUPABASE_ANON_KEY = (import.meta.env.VITE_SUPABASE_ANON_KEY ?? "")
  .trim();

export const SUPABASE_CONFIG_ERROR = (() => {
  if (SUPABASE_URL && SUPABASE_ANON_KEY) return null;

  const missing: string[] = [];
  if (!SUPABASE_URL) missing.push("VITE_SUPABASE_URL");
  if (!SUPABASE_ANON_KEY) missing.push("VITE_SUPABASE_ANON_KEY");

  return (
    `Supabase is not configured. Missing ${missing.join(" and ")}. ` +
    `Set these for the frontend build (Vite). ` +
    `Tip: deployments often provide SUPABASE_URL/SUPABASE_ANON_KEY; ` +
    `this repo maps those to VITE_* during build via vite.config.ts.`
  );
})();

const isBrowser = typeof window !== "undefined";

// Prevent hanging requests (notably auth token refresh) from stalling the app indefinitely.
// Edge Functions (notably signup flows) can sometimes take >8s when they queue and dispatch emails.
const DEFAULT_FETCH_TIMEOUT_MS = 40000;
// Increase timeout for storage operations (uploads/downloads) to 30 minutes
const STORAGE_FETCH_TIMEOUT_MS = 30 * 60 * 1000;

const fetchWithTimeout: typeof fetch = async (input, init) => {
  const controller = new AbortController();
  const existingSignal = init?.signal;

  if (existingSignal) {
    if (existingSignal.aborted) {
      controller.abort();
    } else {
      existingSignal.addEventListener("abort", () => controller.abort(), {
        once: true,
      });
    }
  }

  // Determine if this is a storage request
  let isStorageRequest = false;
  try {
    const urlStr = typeof input === "string"
      ? input
      : input instanceof URL
      ? input.toString()
      : input instanceof Request
      ? input.url
      : "";

    if (urlStr.includes("/storage/v1/object")) {
      isStorageRequest = true;
    }
  } catch {
    // ignore
  }

  const timeoutMs = isStorageRequest
    ? STORAGE_FETCH_TIMEOUT_MS
    : DEFAULT_FETCH_TIMEOUT_MS;

  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

// IMPORTANT: storage keys must not be shared across different Supabase projects/environments.
// Using a constant key can cause stale/foreign refresh tokens to be used against the wrong auth endpoint,
// which can manifest as very slow or hanging getSession()/refresh calls.
const legacyStorageKey = "trade-connector-auth";
const projectScopedStorageKey = (() => {
  try {
    const host = new URL(SUPABASE_URL).host;
    return `tc-auth-${host}`;
  } catch {
    return "tc-auth";
  }
})();

if (isBrowser) {
  // One-way migration: drop the legacy constant key to avoid cross-environment collisions.
  // Users may need to sign in again once.
  try {
    if (window.localStorage.getItem(legacyStorageKey)) {
      window.localStorage.removeItem(legacyStorageKey);
    }
  } catch {
    // ignore
  }
}

// Use globalThis to store instance across HMR reloads in development
declare global {
  var __supabaseInstance: SupabaseClient<Database> | undefined;
}

const createMisconfiguredClient = (): SupabaseClient<Database> => {
  const error = new Error(
    SUPABASE_CONFIG_ERROR ?? "Supabase is not configured",
  );

  return new Proxy({} as SupabaseClient<Database>, {
    get() {
      throw error;
    },
  });
};

// Lazy initialization - only create instance when first accessed
if (!globalThis.__supabaseInstance) {
  globalThis.__supabaseInstance = SUPABASE_CONFIG_ERROR
    ? createMisconfiguredClient()
    : createClient<Database>(SUPABASE_URL, SUPABASE_ANON_KEY, {
      global: {
        fetch: fetchWithTimeout,
      },
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        // Custom lock function to prevent multi-tab auth hangs.
        // NOTE: createClient does NOT pass lockAcquireTimeout through to
        // GoTrueClient, so we provide a custom `lock` function instead.
        // This implements the same timeout+steal pattern that GoTrueClient
        // uses internally when lockAcquireTimeout is set.
        ...(isBrowser && globalThis.navigator?.locks
          ? {
            lock: async <R>(
              name: string,
              acquireTimeout: number,
              fn: () => Promise<R>,
            ): Promise<R> => {
              // acquireTimeout === 0 means "try immediately, skip if busy"
              // (used by auto-refresh ticks — should NOT steal the lock)
              if (acquireTimeout === 0) {
                return await navigator.locks.request(
                  name,
                  { mode: "exclusive", ifAvailable: true },
                  async (lock) => {
                    if (lock) {
                      return await fn();
                    }
                    // Lock not available — return a no-op value.
                    // GoTrueClient's _autoRefreshTokenTick calls lock() with
                    // acquireTimeout=0 to check-and-skip; when the lock is
                    // held, it only needs a graceful bail-out, not an error.
                    // Returning null avoids "Uncaught (in promise)" console
                    // noise that occurs when we throw here, because the
                    // rejection briefly escapes the navigator.locks.request
                    // callback before GoTrueClient's catch handler runs.
                    return null as unknown as R;
                  },
                );
              }

              // acquireTimeout > 0: wait up to the timeout, then steal
              const LOCK_TIMEOUT_MS = 10000; // 10 seconds max
              const effectiveTimeout = acquireTimeout > 0
                ? Math.min(acquireTimeout, LOCK_TIMEOUT_MS)
                : LOCK_TIMEOUT_MS;

              const controller = new AbortController();
              const timeoutId = setTimeout(() => {
                controller.abort();
              }, effectiveTimeout);

              try {
                return await navigator.locks.request(
                  name,
                  { mode: "exclusive", signal: controller.signal },
                  async (lock) => {
                    clearTimeout(timeoutId);
                    if (lock) {
                      return await fn();
                    }
                    // Fallback: lock not granted (shouldn't happen without ifAvailable)
                    return await fn();
                  },
                );
              } catch (err) {
                clearTimeout(timeoutId);
                // If the abort fired, the lock is orphaned — steal it
                if (
                  err instanceof Error &&
                  err.name === "AbortError"
                ) {
                  console.warn(
                    `@supabase/auth: Lock "${name}" was not released within ${effectiveTimeout}ms. ` +
                      `Stealing the lock to recover from a suspended/orphaned tab.`,
                  );
                  return await navigator.locks.request(
                    name,
                    { mode: "exclusive", steal: true },
                    async () => await fn(),
                  );
                }
                throw err;
              }
            },
          }
          : {}),
        ...(isBrowser && typeof window.localStorage !== "undefined"
          ? {
            storage: window.localStorage,
            storageKey: projectScopedStorageKey,
          }
          : {}),
      },
    });
}

// Export the singleton instance (extended with local helper methods)
type SupabaseHelpers = {
  getSafeSession: typeof getSafeSession;
  isAbortError: typeof isAbortError;
};
export const supabase = globalThis.__supabaseInstance! as SupabaseClient<Database> & SupabaseHelpers;

// Attach helpers to the singleton instance for access without named imports (prevents test mock errors)
supabase.getSafeSession = getSafeSession;
supabase.isAbortError = isAbortError;


// ── Deduplicated access token helper ─────────────────────────────────────────
// Many call sites only need the Bearer token for edge function calls.
// Instead of each one calling supabase.auth.getSession() (which acquires
// the Web Locks API lock), they can use this helper which deduplicates
// concurrent requests so only one lock acquisition happens per microtask.

let pendingSessionPromise: ReturnType<typeof supabase.auth.getSession> | null = null;

/**
 * Get the current access token without triggering redundant session lock acquisitions.
 *
 * If a `getSession()` call is already in-flight, this piggybacks on the same
 * promise — so 5 concurrent callers result in only 1 lock acquisition.
 *
 * Use this instead of `supabase.auth.getSession()` when you only need
 * the Bearer token (e.g. for manual `fetch()` calls to edge functions).
 *
 * @returns The access token string, or `null` if not authenticated.
 */
export async function getAccessToken(): Promise<string | null> {
  if (pendingSessionPromise) {
    const result = await pendingSessionPromise;
    return result.data.session?.access_token ?? null;
  }

  try {
    pendingSessionPromise = supabase.auth.getSession();
    const result = await pendingSessionPromise;
    return result.data.session?.access_token ?? null;
  } finally {
    pendingSessionPromise = null;
  }
}

/**
 * Get the current session, deduplicating concurrent lock acquisitions.
 * If the session fetch throws an AbortError (e.g. timeout or abort),
 * rethrows it so that react-query can retry. Other exceptions resolve to null.
 */
export async function getSafeSession(): Promise<Session | null> {
  if (!supabase.auth) return null;

  if (pendingSessionPromise) {
    try {
      const result = await pendingSessionPromise;
      return result.data.session;
    } catch (err) {
      if (isAbortError(err)) throw err;
      return null;
    }
  }

  try {
    pendingSessionPromise = supabase.auth.getSession();
    const result = await pendingSessionPromise;
    return result.data.session;
  } catch (err) {
    if (isAbortError(err)) throw err;
    return null;
  } finally {
    pendingSessionPromise = null;
  }
}

// HMR handling for Vite
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    // Clean up on HMR reload - but keep the global instances
    console.log(
      "🔄 HMR: Supabase client module reloading (instances preserved in globalThis)",
    );
  });
}

// Convenient type exports
type PublicTablesAndViews =
  & Database["public"]["Tables"]
  & Database["public"]["Views"];

export type Tables<T extends keyof PublicTablesAndViews> =
  PublicTablesAndViews[T] extends {
    Row: infer R;
  } ? R
    : never;
export type Enums<T extends keyof Database["public"]["Enums"]> =
  Database["public"]["Enums"][T];
export type Inserts<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Insert"];
export type Updates<T extends keyof Database["public"]["Tables"]> =
  Database["public"]["Tables"][T]["Update"];

/**
 * Detect whether an error is an AbortError (request cancelled due to timeout,
 * navigation, component unmount, or tab backgrounding).
 *
 * Works with both native `Error` instances and Supabase-style error objects
 * that carry a `message` string but are not true `Error` instances.
 */
export function isAbortError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { name?: string; message?: string; code?: string | number };
  if (e.name === "AbortError") return true;
  if (
    typeof e.message === "string" && e.message.includes("signal is aborted")
  ) {
    return true;
  }
  // Supabase wraps abort errors with code 20 (DOMException.ABORT_ERR)
  if (e.code === 20 || e.code === "ABORT_ERR") return true;
  return false;
}

// Re-export the Database type for convenience
export type { Database } from "./database.types";
